/* WC — Mobile swipe/scroll → ACTIVE THUMB (v5.0)
   - Mobile only
   - Reliable scroller pick: prefers .product-thumbnails__viewport[data-scroll-container-viewport] even before overflow is measured
   - Sets ONE marker: .wc-mob-active on the correct li
   - Autoscrolls rail to active thumb (smooth), no flicker
*/

(() => {
  if (!window.matchMedia('(max-width: 959px)').matches) return;

  const onReady = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  };

  const visible = (el) => !!(el && el.getClientRects && el.getClientRects().length);

  const normId = (v) => {
    if (!v) return null;
    const s = String(v);
    const m = s.match(/(\d{5,})\D*$/) || s.match(/(\d{5,})/);
    return m ? m[1] : null;
  };

  const findRoot = () =>
    document.querySelector('#wc-product-root[data-section-type="product"]') ||
    document.querySelector('[data-section-type="product"]');

  onReady(() => {
    const root = findRoot();
    if (!root) return;

    // ---------- RAIL ----------
    const rails = Array.from(
      root.querySelectorAll('.product-thumbnails[data-product-thumbnails], .product-thumbnails')
    ).filter(visible);
    if (!rails.length) return;

    const rail = rails.find(r => r.closest('.below-mobile')) || rails[0];

    const getThumbWraps = () =>
      Array.from(rail.querySelectorAll('.product-thumbnails__item')).filter(visible);

    const getThumbBtn = (wrap) =>
      wrap?.querySelector?.('.product-thumbnails__item-link') ||
      wrap?.querySelector?.('button,a') ||
      null;

    const extractMid = (wrap) => {
      if (!wrap) return null;
      const btn = getThumbBtn(wrap) || wrap;

      return (
        normId(btn.getAttribute?.('data-media-id')) ||
        normId(btn.getAttribute?.('data-thumbnail-id')) ||
        normId(btn.getAttribute?.('data-target')) ||
        normId(btn.getAttribute?.('aria-controls')) ||
        normId(btn.getAttribute?.('href')) ||
        null
      );
    };

    const railScroller = (() => {
      // ALWAYS prefer the viewport scroller if present
      const vp =
        rail.querySelector('.product-thumbnails__viewport[data-scroll-container-viewport]') ||
        rail.querySelector('[data-scroll-container-viewport]') ||
        rail.querySelector('.product-thumbnails__viewport');

      if (vp) return vp;

      // fallback: find best overflow element
      const nodes = [rail, ...Array.from(rail.querySelectorAll('*'))].filter(visible);
      let best = rail, bestOverflow = 0;
      for (const el of nodes) {
        const ov = el.scrollWidth - el.clientWidth;
        if (ov > bestOverflow + 2) { bestOverflow = ov; best = el; }
      }
      return best;
    })();

    const inViewRatio = (wrap) => {
      const vp = railScroller.getBoundingClientRect();
      const r  = wrap.getBoundingClientRect();
      const iw = Math.max(0, Math.min(r.right, vp.right) - Math.max(r.left, vp.left));
      return iw / Math.max(1, r.width);
    };

    const bringIntoView = (wrap) => {
      if (!wrap) return;

      // already mostly visible → don't move rail
      if (inViewRatio(wrap) >= 0.7) return;

      const vp = railScroller.getBoundingClientRect();
      const r  = wrap.getBoundingClientRect();
      const target =
        railScroller.scrollLeft +
        (r.left + r.width / 2) -
        (vp.left + vp.width / 2);

      try { railScroller.scrollTo({ left: target, behavior: 'smooth' }); }
      catch { railScroller.scrollLeft = target; }
    };

    const setActive = (wrap) => {
      if (!wrap) return;

      // ONE marker only
      rail.querySelectorAll('.wc-mob-active').forEach(el => el.classList.remove('wc-mob-active'));
      wrap.classList.add('wc-mob-active');

      // keep aria-current consistent (helps fallback + accessibility)
      getThumbWraps().forEach(w => {
        const b = getThumbBtn(w);
        if (!b) return;
        if (w === wrap) b.setAttribute('aria-current', 'true');
        else if (b.getAttribute('aria-current') === 'true') b.removeAttribute('aria-current');
      });

      bringIntoView(wrap);
    };

    const findBestThumbByMid = (mid) => {
      const wraps = getThumbWraps();
      const cands = wraps.filter(w => extractMid(w) === mid);
      if (!cands.length) return null;

      // prefer most visible candidate in rail
      cands.sort((a, b) => inViewRatio(b) - inViewRatio(a));
      return cands[0];
    };

    // ---------- HERO ----------
    const hero =
      root.querySelector('media-gallery') ||
      root.querySelector('[data-media-gallery]') ||
      root.querySelector('.product__media') ||
      root.querySelector('.product__media-list') ||
      root.querySelector('[id^="MediaGallery-"]') ||
      root.querySelector('[id^="Slider-"]');

    if (!hero) return;

    const heroScroller = (() => {
      const nodes = [hero, ...Array.from(hero.querySelectorAll('*'))].filter(visible);
      let best = hero, bestOverflow = 0;
      for (const el of nodes) {
        const ov = el.scrollWidth - el.clientWidth;
        if (ov > bestOverflow + 2) { bestOverflow = ov; best = el; }
      }
      return best;
    })();

    const heroItems = () => {
      const mids = Array.from(heroScroller.querySelectorAll('[data-media-id]')).filter(visible);
      if (mids.length) return mids;

      return Array.from(heroScroller.querySelectorAll('.media, .product__media-item, .slider__slide, li'))
        .filter(visible);
    };

    const heroMidFromItem = (el) => {
      const host =
        (el?.hasAttribute?.('data-media-id') && el) ||
        el?.closest?.('[data-media-id]') ||
        el?.querySelector?.('[data-media-id]') ||
        el;
      return normId(host?.getAttribute?.('data-media-id')) || null;
    };

    const pickActiveHeroMid = () => {
      const items = heroItems();
      if (!items.length) return null;

      const aria = items.find(el => el.getAttribute('aria-hidden') === 'false');
      if (aria) return heroMidFromItem(aria);

      const sr = heroScroller.getBoundingClientRect();
      const cx = sr.left + sr.width / 2;

      let best = null, bestDist = Infinity;
      for (const el of items) {
        const r = el.getBoundingClientRect();
        const d = Math.abs((r.left + r.width / 2) - cx);
        if (d < bestDist) { bestDist = d; best = el; }
      }
      return best ? heroMidFromItem(best) : null;
    };

    // ---------- SYNC ----------
    let lastMid = null;
    let raf = 0;

    const sync = (reason) => {
      const mid = pickActiveHeroMid();
      if (!mid) return;

      if (mid === lastMid && rail.querySelector('.wc-mob-active')) return;

      const w = findBestThumbByMid(mid);
      if (!w) return;

      setActive(w);
      lastMid = mid;
    };

    const schedule = (reason) => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => sync(reason));
    };

    // init
    [0, 120, 260, 520, 900, 1400].forEach(t => setTimeout(() => schedule('init+' + t), t));

    // hero scroll
    heroScroller.addEventListener('scroll', () => schedule('hero-scroll'), { passive: true });

    // mutations (hero)
    const moHero = new MutationObserver(() => schedule('hero-mutation'));
    moHero.observe(hero, { subtree: true, childList: true, attributes: true, attributeFilter: ['class','style','aria-hidden','data-media-id'] });

    // rail rerender
    const moRail = new MutationObserver((muts) => {
      if (!muts.some(m => m.type === 'childList')) return;
      schedule('rail-childlist');
    });
    moRail.observe(rail, { subtree: true, childList: true });

    // variant change (root + document)
    const featuredMidFromEvent = (e) => {
      const v = e?.detail?.variant || e?.detail?.selectedVariant || e?.detail?.currentVariant || null;
      const fm = v?.featured_media || v?.featuredMedia || v?.featured_image || v?.featuredImage || null;
      return normId(fm?.id || fm) || null;
    };

    const onVariantChange = (e) => {
      const forced = featuredMidFromEvent(e);
      if (forced) {
        const w = findBestThumbByMid(forced);
        if (w) {
          setActive(w);
          lastMid = forced;
          return;
        }
      }
      setTimeout(() => schedule('variant:change'), 160);
    };

    root.addEventListener('variant:change', onVariantChange, true);
    document.addEventListener('variant:change', onVariantChange, true);

    // rail interactions
    rail.addEventListener('click', () => schedule('rail-click'), true);

    // Debug
    window.WC_MobThumb = {
      syncNow() { schedule('manual'); },
      dump() {
        console.group('[WC MobThumb] DUMP');
        console.log('rail:', rail);
        console.log('railScroller:', railScroller);
        console.log('hero:', hero);
        console.log('heroScroller:', heroScroller);
        console.table(getThumbWraps().map((w, i) => ({
          i,
          mid: extractMid(w),
          ratio: inViewRatio(w),
          wc: w.classList.contains('wc-mob-active')
        })));
        console.groupEnd();
      }
    };
  });
})();
